import React from 'react';
import { FiUsers, FiPlus, FiTrash2 } from 'react-icons/fi';
import { generateId } from '@/utils/helpers';
import {
  FormSection,
  SectionTitle,
  SubSectionTitle,
  FormGrid,
  FormGroup,
  Label,
  Input,
  Select,
  ToggleSwitch,
  ToggleInput,
  ToggleLabel,
  InfoBox,
  HelpText,
  DynamicEntryContainer,
  EntryHeader,
  EntryTitle,
  EntryNumber,
  RemoveButton,
  AddButton
} from '../common/FormStyles';
import { InformalReferences, AutrePersonneRespectee } from '@/types/application-forms';
import { UploadedDocument } from '../common/DocumentUpload';
import { Candidate } from '@/models';

interface InformalReferencesSectionProps {
  data: Partial<Candidate>;
  onChange: (data: Partial<Candidate>) => void;
  readonly?: boolean;

  uploadedDocuments?: UploadedDocument[];
    onDocumentUpload?: (sectionKey: string, docType: string, file: File) => Promise<void>;
    onDocumentRemove?: (sectionKey: string, docType: string) => void;
}

export const InformalReferencesSection: React.FC<InformalReferencesSectionProps> = ({
  data,
  onChange,
  readonly = false
}) => {
  const handleChange = (field: string, value: any) => {
    onChange({ ...data, [field]: value });
  };

  const handleAutrePersonneChange = (index: number, field: string, value: any) => {
    const newPersonnes = [...(data.autres_personnes_respectees || [])];
    newPersonnes[index] = { ...newPersonnes[index], [field]: value };
    handleChange('autres_personnes_respectees', newPersonnes);
  };

  const addAutrePersonne = () => {
    const newPersonne: AutrePersonneRespectee = {
      id: generateId(),
      nom: '',
      telephone: '',
      relation: ''
    };
    handleChange('autres_personnes_respectees', [
      ...(data.autres_personnes_respectees || []),
      newPersonne
    ]);
  };

  const removeAutrePersonne = (index: number) => {
    const newPersonnes = (data.autres_personnes_respectees || []).filter((_, i) => i !== index);
    handleChange('autres_personnes_respectees', newPersonnes);
  };

  return (
    <FormSection>
      <SectionTitle>
        <FiUsers />
        Références et Témoignages
      </SectionTitle>

      <InfoBox $type="info">
        <p>
          Les références nous aident à vérifier votre activité et votre sérieux. Plus vous
          aurez de références solides, plus votre dossier sera crédible.
        </p>
      </InfoBox>

      <SubSectionTitle>Qui peut confirmer votre activité ?</SubSectionTitle>

      <FormGroup>
        <Label $required>Chef de quartier / Chef de bloc</Label>
        <Input
          type="text"
          value={data.chef_quartier_nom || ''}
          onChange={e => handleChange('chef_quartier_nom', e.target.value)}
          placeholder="Nom complet"
          disabled={readonly}
        />
      </FormGroup>

      <FormGroup>
        <Label $required>Téléphone du chef de quartier</Label>
        <Input
          type="tel"
          value={data.chef_quartier_telephone || ''}
          onChange={e => handleChange('chef_quartier_telephone', e.target.value)}
          placeholder="+221 X XX XX XX XX"
          disabled={readonly}
        />
        <HelpText>
          Le chef de quartier peut confirmer que vous habitez et travaillez dans la zone
        </HelpText>
      </FormGroup>

      {/* Conditional: Responsable du marché if works at market */}
      <FormGroup>
        <Label>Travaillez-vous dans un marché ?</Label>
        <ToggleSwitch>
          <ToggleInput
            checked={data.travaille_au_marche || false}
            onChange={e => handleChange('travaille_au_marche', e.target.checked)}
            disabled={readonly}
          />
          <ToggleLabel>
            {data.travaille_au_marche ? 'Oui, je travaille dans un marché' : 'Non'}
          </ToggleLabel>
        </ToggleSwitch>
      </FormGroup>

      {data.travaille_au_marche && (
        <>
          <FormGroup>
            <Label>Responsable du marché</Label>
            <Input
              type="text"
              value={data.nom_responsable_marche || ''}
              onChange={e => handleChange('nom_responsable_marche', e.target.value)}
              placeholder="Nom du responsable ou président du marché"
              disabled={readonly}
            />
          </FormGroup>

          <FormGroup>
            <Label>Téléphone du responsable du marché</Label>
            <Input
              type="tel"
              value={data.telephone_responsable_marche || ''}
              onChange={e => handleChange('telephone_responsable_marche', e.target.value)}
              placeholder="+221 X XX XX XX XX"
              disabled={readonly}
            />
          </FormGroup>
        </>
      )}

      <SubSectionTitle>Leader religieux</SubSectionTitle>

      <FormGroup>
        <Label>Type de leader religieux</Label>
        <Select
          value={data.leader_religieux_type || ''}
          onChange={e => handleChange('leader_religieux_type', e.target.value)}
          disabled={readonly}
        >
          <option value="">Sélectionnez...</option>
          <option value="Imam">Imam</option>
          <option value="Pasteur">Pasteur</option>
          <option value="Prêtre">Prêtre</option>
          <option value="Autre">Autre</option>
        </Select>
      </FormGroup>

      <FormGroup>
        <Label>Nom du leader religieux</Label>
        <Input
          type="text"
          value={data.leader_religieux_nom || ''}
          onChange={e => handleChange('leader_religieux_nom', e.target.value)}
          placeholder="Nom complet"
          disabled={readonly}
        />
      </FormGroup>

      <FormGroup>
        <Label>Téléphone du leader religieux</Label>
        <Input
          type="tel"
          value={data.leader_religieux_telephone || ''}
          onChange={e => handleChange('leader_religieux_telephone', e.target.value)}
          placeholder="+221 X XX XX XX XX"
          disabled={readonly}
        />
        <HelpText>
          Un leader religieux peut témoigner de votre moralité et sérieux
        </HelpText>
      </FormGroup>

      <SubSectionTitle>Association professionnelle</SubSectionTitle>

      <FormGroup>
        <Label>Êtes-vous membre d'une association ?</Label>
        <ToggleSwitch>
          <ToggleInput
            checked={data.membre_association || false}
            onChange={e => handleChange('membre_association', e.target.checked)}
            disabled={readonly}
          />
          <ToggleLabel>
            {data.membre_association ? 'Oui, je suis membre' : 'Non'}
          </ToggleLabel>
        </ToggleSwitch>
      </FormGroup>

      {data.membre_association && (
        <>
          <FormGroup>
            <Label>Nom de l'association</Label>
            <Input
              type="text"
              value={data.nom_association || ''}
              onChange={e => handleChange('nom_association', e.target.value)}
              placeholder="Ex: Association des couturiers de Yopougon"
              disabled={readonly}
            />
          </FormGroup>

          <FormGroup>
            <Label>Nom du président de l'association</Label>
            <Input
              type="text"
              value={data.president_association || ''}
              onChange={e => handleChange('president_association', e.target.value)}
              placeholder="Nom complet"
              disabled={readonly}
            />
          </FormGroup>

          <FormGroup>
            <Label>Téléphone du président</Label>
            <Input
              type="tel"
              value={data.telephone_president_association || ''}
              onChange={e => handleChange('telephone_president_association', e.target.value)}
              placeholder="+221 X XX XX XX XX"
              disabled={readonly}
            />
          </FormGroup>
        </>
      )}

      <SubSectionTitle>Fournisseur principal</SubSectionTitle>

      <FormGroup>
        <Label>Nom du fournisseur principal</Label>
        <Input
          type="text"
          value={data.nom_fournisseur_principal || ''}
          onChange={e => handleChange('nom_fournisseur_principal', e.target.value)}
          placeholder="Nom ou entreprise de votre principal fournisseur"
          disabled={readonly}
        />
        <HelpText>
          La personne ou l'entreprise qui vous fournit régulièrement vos matières premières ou produits
        </HelpText>
      </FormGroup>

      <FormGroup>
        <Label>Téléphone du fournisseur</Label>
        <Input
          type="tel"
          value={data.telephone_fournisseur_principal || ''}
          onChange={e => handleChange('telephone_fournisseur_principal', e.target.value)}
          placeholder="+221 X XX XX XX XX"
          disabled={readonly}
        />
      </FormGroup>

      <SubSectionTitle>Autres personnes respectées</SubSectionTitle>

      <InfoBox $type="warning">
        <p>
          Vous pouvez ajouter jusqu'à 3 autres personnes respectées qui peuvent témoigner
          de votre sérieux (voisin influent, ancien employeur, notable du quartier, etc.)
        </p>
      </InfoBox>

      {(data.autres_personnes_respectees || []).map((personne, index) => (
        <DynamicEntryContainer key={personne.id}>
          <EntryHeader>
            <EntryTitle>
              <EntryNumber>{index + 1}</EntryNumber>
              Personne référence {index + 1}
            </EntryTitle>
            <RemoveButton
              onClick={() => removeAutrePersonne(index)}
              disabled={readonly}
            >
              <FiTrash2 />
              Supprimer
            </RemoveButton>
          </EntryHeader>

          <FormGroup>
            <Label>Nom complet</Label>
            <Input
              type="text"
              value={personne.nom || ''}
              onChange={e => handleAutrePersonneChange(index, 'nom', e.target.value)}
              placeholder="Nom et prénoms"
              disabled={readonly}
            />
          </FormGroup>

          <FormGroup>
            <Label>Téléphone</Label>
            <Input
              type="tel"
              value={personne.telephone || ''}
              onChange={e => handleAutrePersonneChange(index, 'telephone', e.target.value)}
              placeholder="+221 X XX XX XX XX"
              disabled={readonly}
            />
          </FormGroup>

          <FormGroup>
            <Label>Votre relation avec cette personne</Label>
            <Input
              type="text"
              value={personne.relation || ''}
              onChange={e => handleAutrePersonneChange(index, 'relation', e.target.value)}
              placeholder="Ex: Voisin depuis 10 ans, Ancien patron, Notable du quartier..."
              disabled={readonly}
            />
          </FormGroup>
        </DynamicEntryContainer>
      ))}

      {!readonly && (data.autres_personnes_respectees || []).length < 3 && (
        <AddButton onClick={addAutrePersonne}>
          <FiPlus />
          Ajouter une personne référence
        </AddButton>
      )}

      <InfoBox $type="info">
        <p>
          <strong>Important :</strong> Toutes les références mentionnées pourront être
          contactées. Assurez-vous d'avoir leur accord avant de les inscrire.
        </p>
      </InfoBox>
    </FormSection>
  );
};
